import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Play, Sparkles, Film, Camera, Users, Award, Briefcase, Smile } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { clients } from '../mock';
import { getProjects, getSettings } from '../api';
import useSeo from '../hooks/useSeo';
import { toEmbedUrl } from '../lib/videoEmbed';
import { optimizeImageUrl } from '../lib/optimizeImage';

const Home = () => {
  useSeo({
    title: 'Motion Designer & Animator in Mansfield, TX (DFW)',
    description: 'Mansfield, TX-based motion designer, animator, video editor & photographer serving the Dallas–Fort Worth Metroplex. 10+ years and 100+ projects delivered for leading brands.',
  });
  const [featuredProjects, setFeaturedProjects] = useState([]);
  const [settings, setSettings] = useState({
    reel_embed_url: 'https://player.vimeo.com/video/450948334?h=37f9a6e9d7&title=0&byline=0&portrait=0',
  });

  useEffect(() => {
    (async () => {
      try {
        const [projects, s] = await Promise.all([getProjects(), getSettings()]);
        setFeaturedProjects(projects.slice(0, 3));
        setSettings(s);
      } catch (e) {
        console.error('Failed to load home data', e);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="home-page">
      <section className="relative lg:min-h-screen flex items-center justify-center pt-32 sm:pt-36 lg:pt-16 pb-12 lg:pb-0 overflow-hidden w-full">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-900/20 to-black"></div>
        <div className="relative z-10 w-full min-w-0 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="hero-title text-[2rem] leading-[1.1] sm:text-5xl lg:text-7xl font-bold mb-6">
            Bringing stories to life —
            <span className="block bg-gradient-to-r from-blue-400 via-blue-400 to-lime-400 bg-clip-text text-transparent">one frame at a time</span>
          </h1>
          <p className="hero-tagline text-base sm:text-lg lg:text-2xl text-gray-300 mb-4">
            Motion Design · Animation · Video Editing · Photography
          </p>
          <p className="hero-blurb text-sm sm:text-base lg:text-lg text-gray-400 max-w-3xl mx-auto mb-8">
            Over a decade of experience crafting compelling visual stories for agencies, marketing teams, and corporate brands. From concept to final delivery, I bring clarity, creativity, and cinematic quality to every project.
          </p>
          <div className="flex flex-row gap-2 sm:gap-4 justify-center items-center mb-8 px-2">
            <Link to="/portfolio" className="flex-1 max-w-[200px]">
              <Button data-testid="hero-view-work-btn" className="hero-button w-full bg-blue-600 hover:bg-blue-700 text-white rounded-full px-4 sm:px-8 py-4 sm:py-5 lg:py-6 text-sm sm:text-base lg:text-lg">
                View Work <ArrowRight className="ml-1 sm:ml-2" size={16} />
              </Button>
            </Link>
            <Link to="/contact" className="flex-1 max-w-[200px]">
              <Button data-testid="hero-quote-btn" variant="outline" className="hero-button w-full border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white rounded-full px-4 sm:px-8 py-4 sm:py-5 lg:py-6 text-sm sm:text-base lg:text-lg">
                Request a Quote
              </Button>
            </Link>
          </div>

          {/* Social proof badges */}
          <div className="flex flex-wrap items-center justify-center gap-x-6 sm:gap-x-8 gap-y-3 mb-10 text-sm sm:text-base text-gray-300" data-testid="hero-social-proof">
            <div className="flex items-center gap-2">
              <Award className="text-blue-400" size={18} />
              <span><span className="text-white font-bold">10+</span> years experience</span>
            </div>
            <span className="hidden sm:inline text-gray-700">·</span>
            <div className="flex items-center gap-2">
              <Briefcase className="text-blue-400" size={18} />
              <span><span className="text-white font-bold">100+</span> projects delivered</span>
            </div>
            <span className="hidden sm:inline text-gray-700">·</span>
            <div className="flex items-center gap-2">
              <Smile className="text-blue-400" size={18} />
              <span><span className="text-white font-bold">50+</span> happy clients</span>
            </div>
          </div>

          {/* Client logo marquee */}
          <div className="relative w-full max-w-5xl mx-auto mb-12 overflow-hidden" data-testid="hero-client-marquee">
            <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-4">Trusted by</p>
            <div className="pointer-events-none absolute inset-y-0 left-0 w-20 sm:w-24 bg-gradient-to-r from-black via-black/80 to-transparent z-10"></div>
            <div className="pointer-events-none absolute inset-y-0 right-0 w-20 sm:w-24 bg-gradient-to-l from-black via-black/80 to-transparent z-10"></div>
            <div className="flex gap-8 animate-marquee w-max">
              {[...clients, ...clients].map((c, idx) => (
                <span key={idx} className="text-sm font-medium text-gray-400 opacity-80 whitespace-nowrap">
                  {c.name}
                </span>
              ))}
            </div>
          </div>

          <div className="demo-reel-container max-w-sm sm:max-w-2xl lg:max-w-4xl mx-auto" data-testid="demo-reel">
            <div className="relative rounded-lg overflow-hidden shadow-2xl bg-gray-900">
              <div className="aspect-video">
                <iframe
                  src={toEmbedUrl(settings.reel_embed_url || settings.reel_url)}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
                  allowFullScreen
                  title="Demo Reel"
                ></iframe>
              </div>
            </div>
            <p className="text-xs sm:text-sm text-gray-400 mt-3 flex items-center justify-center">
              <Play size={14} className="mr-2" /> Watch Demo Reel
            </p>
          </div>
        </div>
      </section>

      <section className="py-14 lg:py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold mb-4">What I Do</h2>
            <p className="text-lg sm:text-xl text-gray-400">Comprehensive creative services for modern brands</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: <Sparkles size={40} />, title: 'Motion Design & Animation', description: '2D & 3D animation, explainer videos, and motion graphics that captivate and engage audiences.' },
              { icon: <Users size={40} />, title: 'Character Design', description: 'Original character concepts and personality-driven animation that brings stories to life.' },
              { icon: <Film size={40} />, title: 'Video Editing', description: 'Professional editing with strong pacing, storytelling, and emotional impact for promotional and corporate content.' },
              { icon: <Camera size={40} />, title: 'Photography', description: 'Events, corporate headshots, real estate, and family photos with a clean, cinematic approach.' },
            ].map((service, index) => (
              <Card key={index} className="bg-gray-800/50 border-gray-700 hover:border-blue-400 transition-all duration-300 hover:transform hover:scale-105">
                <CardContent className="p-8">
                  <div className="text-blue-400 mb-4">{service.icon}</div>
                  <h3 className="text-xl font-semibold mb-3 text-white">{service.title}</h3>
                  <p className="text-gray-400 mb-4">{service.description}</p>
                  <Link to="/services" className="text-blue-400 hover:text-blue-300 font-medium inline-flex items-center">
                    Learn More <ArrowRight className="ml-2" size={16} />
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 lg:py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:justify-between items-start lg:items-center gap-4 lg:gap-0 mb-12">
            <h2 className="text-3xl lg:text-5xl font-bold">Featured Work</h2>
            <Link to="/portfolio">
              <Button variant="outline" className="border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white rounded-full">
                View All Projects <ArrowRight className="ml-2" size={16} />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-testid="featured-projects">
            {featuredProjects.map((project) => (
              <Link key={project.id} to={`/portfolio/${project.slug || project.id}`}>
                <Card className="bg-gray-800/50 border-gray-700 hover:border-blue-400 transition-all duration-300 overflow-hidden group">
                  <div className="relative aspect-video overflow-hidden">
                    <img src={optimizeImageUrl(project.imageUrl, 800)} alt={project.title} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                  </div>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs px-3 py-1 bg-blue-600/20 text-blue-400 rounded-full border border-blue-400/30">{project.category}</span>
                    </div>
                    <h3 className="text-xl font-semibold mb-2 text-white group-hover:text-blue-400 transition-colors">{project.title}</h3>
                    <p className="text-gray-400">{project.description}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 lg:py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">Trusted by Leading Brands</h2>
            <p className="text-gray-400">Proud to have worked with exceptional clients</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {clients.map((client, index) => (
              <div key={index} className="flex items-center justify-center p-6 bg-gray-900/50 rounded-lg border border-gray-800 hover:border-blue-400 transition-colors min-h-[100px]">
                <span className="text-base font-semibold text-gray-300 text-center">{client.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 lg:py-20 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-5xl font-bold mb-6">Ready to bring your story to life?</h2>
          <p className="text-lg sm:text-xl text-gray-400 mb-8">Let's collaborate on your next project. From concept to final delivery, I'm here to help.</p>
          <Link to="/contact">
            <Button data-testid="cta-contact-btn" className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8 py-6 text-lg">
              Get in Touch <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
